import React, { useState, useEffect } from 'react';
import { plansService } from '../services/supabaseService';
import { stripeService } from '../services/stripeService';
import ProtectedRoute from '../components/ProtectedRoute';
import { CreditCard, Loader2 } from 'lucide-react';

// Uses Stripe Checkout (redirect). No Stripe.js on this page, so CSP cannot block payment.
const PaymentPage = () => {
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    plansService.list().then(setPlans);
  }, []);

  const handlePay = async () => {
    if (!selectedPlanId) return;
    setError('');
    setLoading(true);
    const loaderStartedAt = Date.now();
    const MIN_LOADER_MS = 3200; // Keep loader visible until just before redirect so Stripe load feels smoother
    try {
      const origin = window.location.origin;
      const { url } = await stripeService.createCheckoutSession(
        selectedPlanId,
        `${origin}/payment/success`,
        `${origin}/payment`
      );
      const elapsed = Date.now() - loaderStartedAt;
      const waitLeft = Math.max(0, MIN_LOADER_MS - elapsed);
      await new Promise((r) => setTimeout(r, waitLeft));
      window.location.href = url;
    } catch (err) {
      const msg = err.message || 'Could not start payment.';
      setError(msg.includes('Session') || msg.includes('log in') ? msg : `Could not start payment. ${msg}`);
      setLoading(false);
    }
  };

  return (
    <ProtectedRoute>
      <div className="max-w-xl mx-auto px-4 py-16 relative">
        {loading && (
          <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-900/70 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4 p-8 rounded-2xl bg-white shadow-xl max-w-sm text-center">
              <Loader2 className="h-12 w-12 text-primary animate-spin shrink-0" aria-hidden />
              <p className="text-slate-800 font-semibold">Taking you to Stripe...</p>
              <p className="text-slate-500 text-sm">Please wait. The secure payment page will open in a moment — do not close this window.</p>
            </div>
          </div>
        )}

        <h1 className="font-heading font-bold text-2xl text-slate-900 mb-6">Choose a plan</h1>
        {error && (
          <div className="mb-4 p-4 rounded-xl bg-red-50 text-red-700 text-sm">{error}</div>
        )}

        <div className="space-y-4">
          {plans.map((plan) => (
            <label
              key={plan.id}
              className={`block p-4 rounded-2xl border-2 cursor-pointer transition-colors ${
                selectedPlanId === plan.id ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <input
                type="radio"
                name="plan"
                value={plan.id}
                checked={selectedPlanId === plan.id}
                onChange={() => setSelectedPlanId(plan.id)}
                className="sr-only"
              />
              <div className="flex justify-between items-center">
                <span className="font-semibold text-slate-900">{plan.name}</span>
                <span className="text-primary font-bold">₹{(plan.amount_paise / 100).toLocaleString()}</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">{plan.duration_days} days</p>
            </label>
          ))}
        </div>

        {plans.length === 0 && (
          <p className="text-slate-500 py-4">No plans configured. Run Supabase migration first.</p>
        )}

        <button
          type="button"
          onClick={handlePay}
          disabled={!selectedPlanId || loading}
          className="mt-8 w-full py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          ) : (
            <CreditCard className="h-5 w-5" />
          )}
          {loading ? 'Redirecting to Stripe...' : 'Pay with Stripe'}
        </button>

        <p className="mt-4 text-sm text-slate-500 text-center">
          You will be redirected to Stripe to complete payment securely.
        </p>
      </div>
    </ProtectedRoute>
  );
};

export default PaymentPage;
